// deployment_class: customer_env_allowed
/**
 * landingSchemaViews — the ADDITIONAL landing schema views (E3 2026-08-09).
 *
 * The landing's data web can be read four ways. The radial LINEAGE view (the
 * original constellation) answers "where does this number come from?". These three
 * answer questions it cannot:
 *
 *   · FACT CONSTELLATION — which measures the agency actually keeps, and which
 *     dimensions they SHARE. The shared dimension is the whole point: it is what
 *     lets a board question about one fact be answered against another.
 *   · GALAXY SCATTER — the portfolio placed in a dimensional space you choose, so
 *     shape and outliers are visible rather than inferred from a list.
 *   · VENN — which projects sit in more than one call-on-your-attention set, so the
 *     overlaps (the things that need you most) are impossible to miss.
 *
 * ── SSOT DISCIPLINE ──────────────────────────────────────────────────────────
 * Nothing here re-states a figure. The fact/dimension GRAPH and the set PREDICATES
 * are declared (they describe the shape of the agency's data, which is a modelling
 * decision, not a datum); every VALUE is read back out of `CONSTELLATION` /
 * `COCKPITS` by id, and every number is parsed from those same strings. Change a
 * figure in landingDrillData and it changes here — there is no second copy to drift.
 * Per [[no-face-value-data-doctrine]] + [[design-for-resilience-not-around-risk-ssot]].
 *
 * All names + figures remain ILLUSTRATIVE and fictional, exactly as in
 * landingDrillData — the landing is a PUBLIC surface.
 */
import {
  COCKPITS,
  STAGE,
  buildNodeIndex,
  type Cockpit,
  type NodeIndex,
  type NodeStatus,
} from './landingDrillData';

// ─────────────────────────────────────────────────────────────────────────────
// View registry — what the selector offers.
// ─────────────────────────────────────────────────────────────────────────────

export type SchemaViewId = 'lineage' | 'fact' | 'galaxy' | 'venn';

export interface SchemaViewDef {
  id: SchemaViewId;
  /** Selector label. */
  label: string;
  /** The question this view answers — shown under the title. */
  question: string;
}

export const SCHEMA_VIEWS: ReadonlyArray<SchemaViewDef> = [
  { id: 'lineage', label: 'Lineage web', question: 'Where does this number come from?' },
  { id: 'fact', label: 'Fact constellation', question: 'Which measures share which dimensions?' },
  { id: 'galaxy', label: 'Galaxy scatter', question: 'What shape is the portfolio?' },
  { id: 'venn', label: 'Venn', question: 'What needs me in more than one way?' },
];

export function isSchemaViewId(v: string): v is SchemaViewId {
  return SCHEMA_VIEWS.some((s) => s.id === v);
}

// ─────────────────────────────────────────────────────────────────────────────
// Stage geometry.
//
// The lineage web is laid out on a 1160-SQUARE stage (`STAGE`), which the card
// fits by the SMALLER of its two ratios. The card's viewport is wide and short
// (~1120 × ~590), so a square stage fits at ~0.47 — it throws away half the card's
// width and renders every label at under half its authored size.
//
// A radial web needs that squareness. These three views do not: they are naturally
// wide. Giving them a stage in the CARD's own aspect lifts the fit factor to ~0.82,
// which is what makes their type legible at all. The lineage stage is untouched.
// ─────────────────────────────────────────────────────────────────────────────

export const SV_STAGE_W = 1160;
export const SV_STAGE_H = 660;

/** Stage box for a view — the lineage web keeps its square; the rest go wide. */
export function stageSize(view: SchemaViewId): { w: number; h: number } {
  return view === 'lineage' ? { w: STAGE, h: STAGE } : { w: SV_STAGE_W, h: SV_STAGE_H };
}

// ─────────────────────────────────────────────────────────────────────────────
// Derivation helpers — parse the illustrative strings ONCE, here.
// ─────────────────────────────────────────────────────────────────────────────

/** '$62M' → 62 · '$1.5M' → 1.5 · '−$2.1M' → 2.1 (magnitude). Returns 0 if absent. */
export function parseMillions(s: string | undefined): number {
  if (!s) return 0;
  const m = /(\d+(?:\.\d+)?)\s*M/i.exec(s);
  return m?.[1] ? Number(m[1]) : 0;
}

/** '84' → 84 · '84u' → 84 · '1,780 / 1,820' → 1780. Returns 0 if absent. */
export function parseCount(s: string | undefined): number {
  if (!s) return 0;
  const m = /(\d[\d,]*)/.exec(s);
  return m?.[1] ? Number(m[1].replace(/,/g, '')) : 0;
}

/** Read a value straight off the lineage tree, so facts never re-state a figure. */
function treeValue(index: NodeIndex, id: string): string | undefined {
  return index.byId[id]?.value;
}

function treeLabel(index: NodeIndex, id: string): string {
  return index.byId[id]?.label ?? id;
}

/** Fraction of a deal's workflow that is complete — derived from its own steps. */
export function progressOf(c: Cockpit): number {
  if (c.steps.length === 0) return 0;
  return c.steps.filter((s) => s.st === 'done').length / c.steps.length;
}

/** The deal's own RAG tone mapped onto the constellation's status vocabulary. */
function statusOfCockpit(c: Cockpit): NodeStatus {
  return c.rag[0] === 'good' ? 'done' : c.rag[0] === 'bad' ? 'watch' : 'motion';
}

/** Every partnership, with its numbers derived from its own record. */
export interface PartnershipMetrics {
  id: string;
  name: string;
  developer: string;
  structure: string;
  status: NodeStatus;
  /** Total development cost, in $M. */
  tdc: number;
  units: number;
  /** Derived — TDC per unit, in whole dollars. */
  costPerUnit: number;
  /** Derived — completed workflow steps ÷ total steps, 0..1. */
  progress: number;
  blurb: string;
  /** Lineage-tree node this partnership maps to (cross-view continuity). */
  node: string;
}

export function partnershipMetrics(): ReadonlyArray<PartnershipMetrics> {
  return Object.values(COCKPITS).map((c) => {
    const tdc = parseMillions(c.tdc);
    const units = parseCount(c.units);
    return {
      id: c.id,
      name: c.name,
      developer: c.dev,
      structure: c.structure,
      status: statusOfCockpit(c),
      tdc,
      units,
      costPerUnit: units > 0 ? Math.round((tdc * 1_000_000) / units) : 0,
      progress: progressOf(c),
      blurb: c.blurb,
      node: c.node,
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// 1 · FACT CONSTELLATION SCHEMA
//     Multiple fact tables sharing CONFORMED dimensions. The structure is the
//     model; every measure value is read back off the lineage tree by id.
// ─────────────────────────────────────────────────────────────────────────────

export interface SchemaDimension {
  id: string;
  label: string;
  /** What one row of this dimension is. */
  grain: string;
}

export interface SchemaFact {
  id: string;
  label: string;
  /** Lineage node the headline measure is read from (never re-stated here). */
  measureNode: string;
  /** What one row of this fact table is. */
  grain: string;
  status: NodeStatus;
  detail: string;
  /** Conformed dimensions this fact is keyed by. */
  dims: ReadonlyArray<string>;
}

export const SCHEMA_DIMENSIONS: ReadonlyArray<SchemaDimension> = [
  { id: 'd-partnership', label: 'Partnership', grain: 'one active development partnership' },
  { id: 'd-developer', label: 'Developer partner', grain: 'one verified developer firm' },
  { id: 'd-period', label: 'Period', grain: 'one fiscal quarter' },
  { id: 'd-department', label: 'Department', grain: 'one agency department' },
  { id: 'd-objective', label: 'Strategic objective', grain: 'one objective in the 2030 plan' },
];

export const SCHEMA_FACTS: ReadonlyArray<SchemaFact> = [
  {
    id: 'f-pipeline',
    label: 'Development pipeline',
    measureNode: 'pr-pipe',
    grain: 'one partnership, one period',
    status: 'motion',
    detail:
      'Total development cost across the active partnerships. Keyed by partnership, developer and period — which is why a developer question and a pipeline question resolve to the same number.',
    dims: ['d-partnership', 'd-developer', 'd-period'],
  },
  {
    id: 'f-units',
    label: 'Units delivered',
    measureNode: 'pr-units',
    grain: 'one partnership, one period',
    status: 'motion',
    detail:
      'Units placed in service, the measure the 2030 housing goal rolls up from. Shares Partnership and Period with the pipeline fact, so production and cost are comparable without a reconciliation.',
    dims: ['d-partnership', 'd-period', 'd-objective'],
  },
  {
    id: 'f-resolutions',
    label: 'Board resolutions',
    measureNode: 'bd-packet',
    grain: 'one resolution, one meeting',
    status: 'motion',
    detail:
      'Resolutions moving through the board packet. Keyed by partnership and department, so "what is the board being asked about this deal?" is answerable from the deal side.',
    dims: ['d-partnership', 'd-department', 'd-period'],
  },
  {
    id: 'f-compliance',
    label: 'Compliance events',
    measureNode: 'ov-comp',
    grain: 'one event, one department',
    status: 'watch',
    detail:
      'Open compliance flags and certifications. Conformed on Department and Objective — the governance priority reads directly off this fact.',
    dims: ['d-department', 'd-objective', 'd-period'],
  },
  {
    id: 'f-distributions',
    label: 'Distributions',
    measureNode: 'in-fee',
    grain: 'one distribution event, one partnership',
    status: 'motion',
    detail:
      'Deferred developer fee and cash movement across the portfolio. Shares Partnership and Developer with the pipeline fact — the waterfall and the pipeline cannot disagree.',
    dims: ['d-partnership', 'd-developer', 'd-period'],
  },
];

export interface PlacedFact extends SchemaFact {
  x: number;
  y: number;
  /** Headline measure, read off the lineage tree. */
  value: string;
}
export interface PlacedDimension extends SchemaDimension {
  x: number;
  y: number;
  /** How many facts are keyed by this dimension — the "conformed" readout. */
  factCount: number;
}
export interface PlacedSchemaLink {
  id: string;
  factId: string;
  dimId: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}
export interface FactConstellationLayout {
  facts: ReadonlyArray<PlacedFact>;
  dims: ReadonlyArray<PlacedDimension>;
  links: ReadonlyArray<PlacedSchemaLink>;
}

/** Facts on an inner ELLIPSE, conformed dimensions on an outer one, offset by a half
 *  step so the spokes never run straight through a neighbouring box. Elliptical
 *  rather than circular because the stage is wide and short — a circle would stack
 *  the boxes vertically and waste the width. Deterministic. */
export function layoutFactConstellation(index: NodeIndex): FactConstellationLayout {
  const CX = SV_STAGE_W / 2;
  const CY = SV_STAGE_H / 2;
  const FACT_RX = 300;
  const FACT_RY = 156;
  const DIM_RX = 528;
  const DIM_RY = 284;

  const facts: PlacedFact[] = SCHEMA_FACTS.map((f, i) => {
    const a = (i / SCHEMA_FACTS.length) * 2 * Math.PI - Math.PI / 2;
    return {
      ...f,
      x: CX + FACT_RX * Math.cos(a),
      y: CY + FACT_RY * Math.sin(a),
      value: treeValue(index, f.measureNode) ?? '',
    };
  });

  const dims: PlacedDimension[] = SCHEMA_DIMENSIONS.map((d, i) => {
    // Half-step offset from the fact ring.
    const a = ((i + 0.5) / SCHEMA_DIMENSIONS.length) * 2 * Math.PI - Math.PI / 2;
    return {
      ...d,
      x: CX + DIM_RX * Math.cos(a),
      y: CY + DIM_RY * Math.sin(a),
      factCount: SCHEMA_FACTS.filter((f) => f.dims.includes(d.id)).length,
    };
  });

  const byId = new Map<string, { x: number; y: number }>();
  for (const f of facts) byId.set(f.id, { x: f.x, y: f.y });
  for (const d of dims) byId.set(d.id, { x: d.x, y: d.y });

  const links: PlacedSchemaLink[] = [];
  for (const f of SCHEMA_FACTS) {
    for (const dimId of f.dims) {
      const a = byId.get(f.id);
      const b = byId.get(dimId);
      if (!a || !b) continue;
      links.push({ id: `${f.id}→${dimId}`, factId: f.id, dimId, x1: a.x, y1: a.y, x2: b.x, y2: b.y });
    }
  }

  return { facts, dims, links };
}

// ─────────────────────────────────────────────────────────────────────────────
// 2 · DIMENSIONAL SCATTER / GALAXY SCHEMA
//     The portfolio in a dimensional space the viewer picks. Axes are real
//     measures derived from each partnership's own record.
// ─────────────────────────────────────────────────────────────────────────────

export interface GalaxyAxis {
  id: string;
  label: string;
  /** Pull the value off a partnership's derived metrics. */
  get: (p: PartnershipMetrics) => number;
  /** Render a value for the axis ticks + the detail readout. */
  format: (n: number) => string;
}

export const GALAXY_AXES: ReadonlyArray<GalaxyAxis> = [
  {
    id: 'tdc',
    label: 'Total development cost',
    get: (p) => p.tdc,
    format: (n) => `$${n % 1 === 0 ? n : n.toFixed(1)}M`,
  },
  { id: 'units', label: 'Units', get: (p) => p.units, format: (n) => `${Math.round(n)}` },
  {
    id: 'costPerUnit',
    label: 'Cost per unit',
    get: (p) => p.costPerUnit,
    format: (n) => `$${Math.round(n / 1000)}k`,
  },
  {
    id: 'progress',
    label: 'Workflow progress',
    get: (p) => p.progress * 100,
    format: (n) => `${Math.round(n)}%`,
  },
];

export function galaxyAxis(id: string): GalaxyAxis {
  return GALAXY_AXES.find((a) => a.id === id) ?? GALAXY_AXES[0]!;
}

export interface GalaxyPoint {
  id: string;
  name: string;
  x: number;
  y: number;
  /** Bubble radius in stage units — derived from total development cost. */
  r: number;
  status: NodeStatus;
  xValue: string;
  yValue: string;
  metrics: PartnershipMetrics;
}

export interface GalaxyTick {
  /** Stage coordinate along the axis. */
  pos: number;
  label: string;
}

export interface GalaxyLayout {
  points: ReadonlyArray<GalaxyPoint>;
  xTicks: ReadonlyArray<GalaxyTick>;
  yTicks: ReadonlyArray<GalaxyTick>;
  /** Plot rectangle in stage coordinates. */
  plot: { left: number; top: number; right: number; bottom: number };
  /** Mean of the Y measure — the reference line ("where the portfolio sits"). */
  yMean: number;
  yMeanLabel: string;
}

const PLOT = { left: 138, top: 92, right: SV_STAGE_W - 62, bottom: SV_STAGE_H - 92 };
const TICKS = 4;

/** Pad a [min,max] range so points never sit on the axis, and never divide by zero. */
function niceRange(values: ReadonlyArray<number>): { min: number; max: number } {
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return { min: 0, max: 1 };
  if (hi === lo) return { min: lo === 0 ? 0 : lo * 0.8, max: lo === 0 ? 1 : lo * 1.2 };
  const pad = (hi - lo) * 0.18;
  return { min: lo - pad, max: hi + pad };
}

export function layoutGalaxy(xAxisId: string, yAxisId: string): GalaxyLayout {
  const ps = partnershipMetrics();
  const xa = galaxyAxis(xAxisId);
  const ya = galaxyAxis(yAxisId);

  const xr = niceRange(ps.map((p) => xa.get(p)));
  const yr = niceRange(ps.map((p) => ya.get(p)));

  const sx = (v: number): number =>
    PLOT.left + ((v - xr.min) / (xr.max - xr.min)) * (PLOT.right - PLOT.left);
  // Y grows upward on screen.
  const sy = (v: number): number =>
    PLOT.bottom - ((v - yr.min) / (yr.max - yr.min)) * (PLOT.bottom - PLOT.top);

  // Bubble size encodes total development cost — area-proportional (√), so a deal
  // twice the cost reads twice the size rather than four times it. Normalised across
  // the ACTUAL cost range so the spread uses the whole visual scale: taking √(t/max)
  // straight compresses a $33M–$62M portfolio into a barely-separable 52–62px band.
  const tdcs = ps.length ? ps.map((p) => p.tdc) : [1];
  const rootLo = Math.sqrt(Math.max(0, Math.min(...tdcs)));
  const rootHi = Math.sqrt(Math.max(1, Math.max(...tdcs)));
  const rootSpan = rootHi - rootLo || 1;
  const R_MIN = 24;
  const R_MAX = 64;

  const points: GalaxyPoint[] = ps.map((p) => ({
    id: p.id,
    name: p.name,
    x: sx(xa.get(p)),
    y: sy(ya.get(p)),
    r: R_MIN + (R_MAX - R_MIN) * ((Math.sqrt(p.tdc) - rootLo) / rootSpan),
    status: p.status,
    xValue: xa.format(xa.get(p)),
    yValue: ya.format(ya.get(p)),
    metrics: p,
  }));

  const mkTicks = (
    r: { min: number; max: number },
    axis: GalaxyAxis,
    scale: (v: number) => number,
  ): GalaxyTick[] =>
    Array.from({ length: TICKS + 1 }, (_, i) => {
      const v = r.min + ((r.max - r.min) * i) / TICKS;
      return { pos: scale(v), label: axis.format(v) };
    });

  const yValues = ps.map((p) => ya.get(p));
  const yMean = yValues.reduce((a, b) => a + b, 0) / (yValues.length || 1);

  return {
    points,
    xTicks: mkTicks(xr, xa, sx),
    yTicks: mkTicks(yr, ya, sy),
    plot: PLOT,
    yMean: sy(yMean),
    yMeanLabel: `portfolio average · ${ya.format(yMean)}`,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 3 · VENN — the call-on-your-attention sets.
//     Membership is COMPUTED by predicate over each partnership's own record.
//     Nothing is hand-listed, so a deal that stops needing attention leaves the
//     set on its own.
// ─────────────────────────────────────────────────────────────────────────────

export interface VennSetDef {
  id: string;
  label: string;
  /** Why a project lands in this set — shown in the legend + the detail. */
  rule: string;
  member: (p: PartnershipMetrics, c: Cockpit, index: NodeIndex) => boolean;
}

/** A partnership is named in a board resolution when a resolution row in the
 *  lineage tree carries its name — read from the tree, not from a second list. */
function namedInResolution(p: PartnershipMetrics, index: NodeIndex): boolean {
  const packetKids = index.childIdsOf['bd-packet'] ?? [];
  return packetKids.some((id) => treeLabel(index, id).includes(p.name));
}

export const VENN_SETS: ReadonlyArray<VennSetDef> = [
  {
    id: 'attention',
    label: 'Needs attention',
    rule: 'the deal is not green — a gate, a gap or a date is live',
    member: (p) => p.status !== 'done',
  },
  {
    id: 'board',
    label: 'Board action',
    rule: 'a resolution in the current packet names this deal',
    member: (p, _c, index) => namedInResolution(p, index),
  },
  {
    id: 'money',
    label: 'Money at risk',
    rule: 'a figure on the deal has breached its floor',
    member: (_p, c) => c.kpis.some((k) => k.tone === 'bad'),
  },
];

export interface VennMember {
  id: string;
  name: string;
  /** Ids of every set this project belongs to. */
  setIds: ReadonlyArray<string>;
  metrics: PartnershipMetrics;
}

export interface VennPlacedSet {
  id: string;
  label: string;
  rule: string;
  cx: number;
  cy: number;
  r: number;
  /** Where the set's own title sits (outside the circle). */
  labelX: number;
  labelY: number;
  count: number;
}

export interface VennRegion {
  /** Sorted set ids that define this region — '' for the outside. */
  key: string;
  setIds: ReadonlyArray<string>;
  x: number;
  y: number;
  members: ReadonlyArray<VennMember>;
}

export interface VennLayout {
  sets: ReadonlyArray<VennPlacedSet>;
  regions: ReadonlyArray<VennRegion>;
  members: ReadonlyArray<VennMember>;
}

/** Region centroids for the standard symmetric 3-circle Venn, in stage units and
 *  keyed by sorted membership. Derived from the circle geometry below. */
export function layoutVenn(index: NodeIndex): VennLayout {
  const CX = SV_STAGE_W / 2;
  // Biased up, leaving room under the circles for the outside region + the rules.
  const CY = 318;
  const R = 150;
  // Three circles on an equilateral triangle — the canonical symmetric Venn.
  // Sized so the topmost TITLE (which sits R + LABEL_GAP out along the radial, not
  // just the circle edge) still clears the top of the stage.
  const D = 100; // centre-to-centroid distance
  const LABEL_GAP = 42;
  const centres = [
    { x: CX, y: CY - D },                                   // top      · set 0
    { x: CX - D * Math.cos(Math.PI / 6), y: CY + D / 2 },   // bottom-L · set 1
    { x: CX + D * Math.cos(Math.PI / 6), y: CY + D / 2 },   // bottom-R · set 2
  ];

  const ps = partnershipMetrics();
  const members: VennMember[] = ps.map((p) => {
    const c = COCKPITS[p.id]!;
    return {
      id: p.id,
      name: p.name,
      setIds: VENN_SETS.filter((s) => s.member(p, c, index)).map((s) => s.id),
      metrics: p,
    };
  });

  const sets: VennPlacedSet[] = VENN_SETS.map((s, i) => {
    const c = centres[i]!;
    // Push the title straight out from the CENTROID so it clears every circle.
    const dx = c.x - CX;
    const dy = c.y - CY;
    const len = Math.hypot(dx, dy) || 1;
    return {
      id: s.id,
      label: s.label,
      rule: s.rule,
      cx: c.x,
      cy: c.y,
      r: R,
      labelX: c.x + (dx / len) * (R + LABEL_GAP),
      labelY: c.y + (dy / len) * (R + LABEL_GAP),
      count: members.filter((m) => m.setIds.includes(s.id)).length,
    };
  });

  // The seven inside regions + the outside, each with a centroid to stack chips at.
  //
  // A two-set lens is NOT at the midpoint of its two circle centres — that point sits
  // barely off the triple intersection, and at this radius the two chips collide. Push
  // each lens outward, away from the figure's centroid, so every region is visibly its
  // own place.
  const ids = VENN_SETS.map((s) => s.id);
  const pushOut = (x: number, y: number, by: number): { x: number; y: number } => {
    const dx = x - CX;
    const dy = y - CY;
    const len = Math.hypot(dx, dy) || 1;
    return { x: x + (dx / len) * by, y: y + (dy / len) * by };
  };
  /** Lens between two circles: their midpoint, pushed clear of the triple region. */
  const lens = (a: number, b: number): { x: number; y: number } =>
    pushOut((centres[a]!.x + centres[b]!.x) / 2, (centres[a]!.y + centres[b]!.y) / 2, R * 0.42);
  /** The part of a circle belonging to it alone: pushed furthest out. */
  const only = (i: number): { x: number; y: number } =>
    pushOut(centres[i]!.x, centres[i]!.y, R * 0.5);

  const regionSpec: ReadonlyArray<{ sets: string[]; x: number; y: number }> = [
    { sets: [ids[0]!], ...only(0) },
    { sets: [ids[1]!], ...only(1) },
    { sets: [ids[2]!], ...only(2) },
    { sets: [ids[0]!, ids[1]!], ...lens(0, 1) },
    { sets: [ids[0]!, ids[2]!], ...lens(0, 2) },
    { sets: [ids[1]!, ids[2]!], ...lens(1, 2) },
    { sets: [ids[0]!, ids[1]!, ids[2]!], x: CX, y: CY + 4 },
    // Outside every circle — parked bottom-left, clear of the circles and of the
    // centred rules panel. "Nothing here is waiting on you" is a real readout, so it
    // gets a real place rather than being dropped off the diagram.
    { sets: [], x: 168, y: SV_STAGE_H - 96 },
  ];

  const keyOf = (s: ReadonlyArray<string>): string => [...s].sort().join('+');

  const regions: VennRegion[] = regionSpec.map((spec) => {
    const key = keyOf(spec.sets);
    return {
      key,
      setIds: spec.sets,
      x: spec.x,
      y: spec.y,
      members: members.filter((m) => keyOf(m.setIds) === key),
    };
  });

  return { sets, regions, members };
}

/** Convenience for the views — one shared index build per open. */
export function schemaIndex(): NodeIndex {
  return buildNodeIndex();
}
